'use client';

import {
  type AdaptiveScanningResumeMode,
  type AdaptiveScanningStateDto,
  type AdaptiveScanningTargetBlockReason,
} from '@terra/shared-contracts-applications';
import { HStack, VStack, css } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '../../components/button';
import { Checkbox } from '../../components/checkbox';
import { LegacyDialog } from '../../components/dialog';
import { IconButton } from '../../components/icon-button';
import { BodyDefault, HeadingModal } from '../../components/typography';
import { selectAdaptiveResumeReasons } from './select-adaptive-resume-reasons';

const TARGET_REASONS: AdaptiveScanningTargetBlockReason[] = [
  'waf',
  'rate_limit',
  'anti_bot_system',
  'server_unreachable',
];

const checkboxLabelStyles = css({
  '& [data-part="label"]': {
    fontWeight: 'normal',
  },
  '&[data-disabled]': {
    cursor: 'not-allowed',
  },
  '&[data-disabled] [data-part="label"]': {
    color: 'fg.disabled',
  },
  '&[data-disabled] [data-part="control"]': {
    borderColor: 'border.disabled',
    background: 'bg.disabled',
  },
});

export type AdaptiveScanningResumeDialogProps = {
  open: boolean;
  state: AdaptiveScanningStateDto;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (input: {
    mode: AdaptiveScanningResumeMode;
    rerunBlockedReasons: AdaptiveScanningTargetBlockReason[];
  }) => void;
};

export function AdaptiveScanningResumeDialog({
  open,
  state,
  isSubmitting,
  onOpenChange,
  onConfirm,
}: AdaptiveScanningResumeDialogProps) {
  const t = useTranslations('designSystem.adaptiveScanning');
  const tCommon = useTranslations('designSystem.common');
  const tBlocked = useTranslations(
    'designSystem.signals.lifecycle.status.blockedReason',
  );
  const [mode, setMode] = useState<AdaptiveScanningResumeMode>('cautious');
  const [selectedReasons, setSelectedReasons] = useState<
    AdaptiveScanningTargetBlockReason[]
  >(() => selectAdaptiveResumeReasons(state));

  useEffect(() => {
    if (!open) {
      return;
    }

    setMode('cautious');
    setSelectedReasons(selectAdaptiveResumeReasons(state));
  }, [open, state]);

  const plannedCount = selectedReasons.reduce((total, reason) => {
    const match = state.blockedReasonCounts.find(
      (entry) => entry.reason === reason,
    );

    return total + (match?.count ?? 0);
  }, 0);

  return (
    <LegacyDialog.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content width="512px" maxWidth="90%">
          <VStack gap={3} alignItems="stretch" px={6} pt={6} pb={3}>
            <HStack justifyContent="space-between" alignItems="center">
              <HeadingModal>{t('resumeTitle')}</HeadingModal>
              <LegacyDialog.CloseTrigger asChild>
                <IconButton
                  aria-label={tCommon('close')}
                  variant="ghost"
                  size="xs"
                >
                  <Cancel01Icon />
                </IconButton>
              </LegacyDialog.CloseTrigger>
            </HStack>
            <BodyDefault color="fg.muted">{t('resumeDescription')}</BodyDefault>
            <VStack alignItems="stretch" gap={2}>
              {TARGET_REASONS.map((reason) => {
                const count =
                  state.blockedReasonCounts.find(
                    (entry) => entry.reason === reason,
                  )?.count ?? 0;

                return (
                  <Checkbox
                    key={reason}
                    className={checkboxLabelStyles}
                    checked={selectedReasons.includes(reason)}
                    disabled={count === 0}
                    onCheckedChange={(details) => {
                      const checked = details.checked === true;

                      setSelectedReasons((current) =>
                        checked
                          ? [...current, reason]
                          : current.filter((item) => item !== reason),
                      );
                    }}
                  >
                    <BodyDefault
                      as="span"
                      color={count === 0 ? 'fg.disabled' : undefined}
                    >
                      {tBlocked(reason)} ({count})
                    </BodyDefault>
                  </Checkbox>
                );
              })}
            </VStack>
            <Checkbox
              className={checkboxLabelStyles}
              checked={mode === 'continue'}
              onCheckedChange={(details) => {
                setMode(details.checked === true ? 'continue' : 'cautious');
              }}
            >
              <BodyDefault as="span">{t('continueMode')}</BodyDefault>
            </Checkbox>
            <BodyDefault color="fg.muted">
              {plannedCount > 0
                ? t('plannedWave', { count: Math.min(plannedCount, 3) })
                : t('noBlockedSignals')}
            </BodyDefault>
          </VStack>
          <HStack gap={2} justifyContent="flex-end" pt={3} pb={6} px={6}>
            <LegacyDialog.CloseTrigger asChild>
              <Button variant="outline" size="sm">
                {tCommon('cancel')}
              </Button>
            </LegacyDialog.CloseTrigger>
            <Button
              size="sm"
              disabled={
                isSubmitting ||
                (mode !== 'continue' &&
                  (selectedReasons.length === 0 || plannedCount === 0))
              }
              onClick={() =>
                onConfirm({
                  mode,
                  rerunBlockedReasons: selectedReasons,
                })
              }
            >
              {t('resume')}
            </Button>
          </HStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
